import { asyncThunkCreator, buildCreateSlice } from "@reduxjs/toolkit"
import { createSelector } from "reselect"

import { selectEmbedded, selectOrganization } from "@/lib/storage/app-slice"
import { selectEmbeddedView } from "@/lib/storage/embedded-slice"
import {
  selectCanAdminVanityLinkPermission,
  selectCanViewAllVanityReportsPermission,
  selectUserIsAnonymous,
} from "@/lib/storage/user-slice"
import { selectCanAdminInspirationPermission } from "./inspiration-slice"

const createThunkSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
})

export type NavValue =
  | ""
  | "/agents"
  | "/chat"
  | "/explore"
  | "/saved"
  | "/trips"
  | "/inspiration"
  | "/create"
  | "/dashboard"
  | "/admin"
  | "/you"
  | "/updates"

export interface SiteNavSliceState {
  profileMenuOpen: boolean
  burgerOpen: boolean
  navValue: NavValue
  mobileNavVisible: boolean
  chatListDialogOpen: boolean
  navSearchTerm: string
  chatTabResumeChatId: number | null
}

export const siteNavInitialState: SiteNavSliceState = {
  profileMenuOpen: false,
  burgerOpen: false,
  navValue: "",
  mobileNavVisible: false,
  chatListDialogOpen: false,
  navSearchTerm: "",
  chatTabResumeChatId: null,
}

const siteNavSlice = createThunkSlice({
  name: "siteNavSlice",
  initialState: siteNavInitialState,
  reducers: (create) => ({
    initSiteNav: create.reducer<void>((state) => {
      state.profileMenuOpen = false
      state.burgerOpen = false
      state.navValue = ""
      state.chatTabResumeChatId = null
    }),
    // A navigation landing inside the persistent shell. Unlike initSiteNav it
    // keeps chatTabResumeChatId: the mobile Chat tab resumes that chat after
    // the user has navigated away from it.
    closeNavMenus: create.reducer<void>((state) => {
      state.profileMenuOpen = false
      state.burgerOpen = false
      state.navValue = ""
    }),
    setProfileMenuOpen: create.reducer<boolean>((state, action) => {
      state.profileMenuOpen = action.payload
    }),
    setBurgerOpen: create.reducer<boolean>((state, action) => {
      state.burgerOpen = action.payload
    }),
    setNavValue: create.reducer<NavValue>((state, action) => {
      state.navValue = action.payload
    }),
    setMobileNavVisible: create.reducer<boolean>((state, action) => {
      state.mobileNavVisible = action.payload
    }),
    setChatListDialogOpen: create.reducer<boolean>((state, action) => {
      state.chatListDialogOpen = action.payload
    }),
    setNavSearchTerm: create.reducer<string>((state, action) => {
      state.navSearchTerm = action.payload
    }),
    setChatTabResumeChatId: create.reducer<number | null>((state, action) => {
      state.chatTabResumeChatId = action.payload
    }),
  }),
  selectors: {
    selectProfileMenuOpen: (state) => state.profileMenuOpen,
    selectBurgerOpen: (state) => state.burgerOpen,
    selectNavValue: (state) => state.navValue,
    selectMobileNavVisible: (state) => state.mobileNavVisible,
    selectChatListDialogOpen: (state) => state.chatListDialogOpen,
    selectNavSearchTerm: (state) => state.navSearchTerm || undefined,
    selectChatTabResumeChatId: (state) => state.chatTabResumeChatId,
  },
})

export const {
  selectProfileMenuOpen,
  selectBurgerOpen,
  selectNavValue,
  selectMobileNavVisible,
  selectChatListDialogOpen,
  selectNavSearchTerm,
  selectChatTabResumeChatId,
} = siteNavSlice.selectors

export const {
  initSiteNav,
  closeNavMenus,
  setBurgerOpen,
  setProfileMenuOpen,
  setNavValue,
  setMobileNavVisible,
  setChatListDialogOpen,
  setNavSearchTerm,
  setChatTabResumeChatId,
} = siteNavSlice.actions

export const selectHasAdminLink = createSelector(
  [
    selectEmbedded,
    selectCanAdminInspirationPermission,
    selectCanAdminVanityLinkPermission,
    selectCanViewAllVanityReportsPermission,
  ],
  (
    embedded,
    adminInspirationPermission,
    adminVanityLinkPermission,
    viewAllVanityReportsPermission
  ) =>
    !embedded &&
    (adminInspirationPermission ||
      adminVanityLinkPermission ||
      viewAllVanityReportsPermission)
)

export const selectHasInspirationLink = createSelector(
  [selectEmbedded, selectOrganization],
  (embedded, organization) =>
    !(embedded && organization?.properties.hide_inspirations)
)

export const selectHasExploreLink = createSelector(
  [selectEmbedded, selectOrganization],
  (embedded, organization) =>
    !embedded || !!organization?.properties.show_explore_page
)

// hide navigation for anonymous user viewing a guide on a customer-copies org
export const selectGuideOnlyNav = createSelector(
  [selectEmbeddedView, selectOrganization, selectUserIsAnonymous],
  (embeddedView, organization, isAnonymous) =>
    embeddedView.type === "guide" &&
    isAnonymous &&
    !!organization?.properties.customer_copies_enabled
)

export default siteNavSlice
